import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";
import { minutes } from "@/utils";

interface Exchange {
  id: string;
  name: string;
  type: string;
  logo: string;
  thumb: string;
  keyRequired: boolean;
  secretRequired: boolean;
  additionalFields: string[];
  api_required_fields: ExchangeApiRequiredField[];
  tag: ExchangeTag | string | null | undefined;
}

export enum ExchangeTag {
  EXCHANGE = "exchange",
  BLOCKCHAIN = "blockchain",
  WALLET = "wallet",
}

export {
  ExchangeApiRequiredField,
  normalizeApiRequiredFields,
} from "./apiRequiredFields";
import { ExchangeApiRequiredField } from "./apiRequiredFields";

export const getNameForField = (tag: string, fieldType: string): string => {
  const keyPlaceholderMap: Record<ExchangeTag, Record<string, string>> = {
    [ExchangeTag.EXCHANGE]: {
        [ExchangeApiRequiredField.ACCOUNT_ID]: "Account ID",
      [ExchangeApiRequiredField.KEY]: "API key",
      [ExchangeApiRequiredField.SECRET]: "API secret",
      [ExchangeApiRequiredField.PASSWORD]: "API password (passphrase)",
      [ExchangeApiRequiredField.BLOCKCHAIN]: "Blockchain Type",
    },
    [ExchangeTag.BLOCKCHAIN]: {
      [ExchangeApiRequiredField.KEY]: "Blockchain address (Public key)",
      [ExchangeApiRequiredField.BLOCKCHAIN]: "Blockchain Type",
    },
    [ExchangeTag.WALLET]: {
      [ExchangeApiRequiredField.KEY]: "Wallet address (Public key)",
      [ExchangeApiRequiredField.BLOCKCHAIN]: "Blockchain Type",
    },
  };

  return keyPlaceholderMap?.[tag]?.[fieldType] ?? `${fieldType}`;
};

export const getDescriptionForField = (
  tag: string,
  fieldType: string
): string => {
  const keyPlaceholderMap: Record<ExchangeTag, Record<string, string>> = {
    [ExchangeTag.EXCHANGE]: {
      [ExchangeApiRequiredField.ACCOUNT_ID]:
        "Your exchange-provided account id, user id, customer id or organization_id used to access your account",
      [ExchangeApiRequiredField.KEY]:
        "Your exchange-provided API key used to access your account programmatically.",
      [ExchangeApiRequiredField.SECRET]:
        "The secret key paired with your API key. Keep it private and secure.",
      [ExchangeApiRequiredField.PASSWORD]:
        "Some exchanges require an extra passphrase or password in addition to your API key and secret. Enter it here if needed.",
      [ExchangeApiRequiredField.BLOCKCHAIN]:
        "Select which blockchain type your wallet address is for. Different blockchains use different address formats.",
    },
    [ExchangeTag.BLOCKCHAIN]: {
      [ExchangeApiRequiredField.KEY]:
        "Your public blockchain address. This is the address where you receive tokens and can be shared with others.",
      [ExchangeApiRequiredField.BLOCKCHAIN]:
        "Select which blockchain this address is for.",
    },
    [ExchangeTag.WALLET]: {
      [ExchangeApiRequiredField.KEY]:
        "Your wallet address (public key) used to track your assets. This address is safe to share and needed to sync balances.",
      [ExchangeApiRequiredField.BLOCKCHAIN]:
        "Select which blockchain your wallet address is for. Wallets support multiple blockchains with different address formats.",
    },
  };

  return (
    keyPlaceholderMap?.[tag]?.[fieldType] ??
    `Please enter a valid value for ${fieldType}`
  );
};

export const getPlaceholderForField = (
  tag: string,
  fieldType: string
): string => {
  const keyPlaceholderMap: Record<ExchangeTag, Record<string, string>> = {
    [ExchangeTag.EXCHANGE]: {
      [ExchangeApiRequiredField.ACCOUNT_ID]:
        "Enter account id, user id, customer id or organization_id",
      [ExchangeApiRequiredField.KEY]: "Enter API key",
      [ExchangeApiRequiredField.SECRET]: "Enter API secret",
      [ExchangeApiRequiredField.PASSWORD]: "Enter API password (passphrase)",
      [ExchangeApiRequiredField.BLOCKCHAIN]: "Select blockchain type...",
    },
    [ExchangeTag.BLOCKCHAIN]: {
      [ExchangeApiRequiredField.KEY]: "Enter blockchain address (public key)",
      [ExchangeApiRequiredField.BLOCKCHAIN]: "Select blockchain type...",
    },
    [ExchangeTag.WALLET]: {
      [ExchangeApiRequiredField.KEY]: "Enter wallet address (public key)",
      [ExchangeApiRequiredField.BLOCKCHAIN]: "Select blockchain type...",
    },
  };

  return keyPlaceholderMap?.[tag]?.[fieldType] ?? `Enter ${fieldType}`;
};

interface UseGetExchangeProps {
  exchangeId?: number | string;
  integrationId?: string;
  refetchOnWindowFocus?: boolean;
  staleTime?: number;
}

export const useExchangeQueryKey = (exchangeId?: number | string, integrationId?: string) =>
  ["exchange", exchangeId, integrationId] as const;

export const useGetExchange = ({
  exchangeId,
  integrationId,
  refetchOnWindowFocus = true,
  staleTime = 1 * minutes,
}: UseGetExchangeProps): {
  isQueryLoading: boolean;
  isIdle: boolean;
  error: unknown;
  exchange?: Exchange;
  refetch: () => void;
} => {
  const { user, getToken } = useAuth();
  const email = user?.email;

  const getExchange = async (): Promise<Exchange> => {
    try {
      const token = await getToken();

      // Use integration_id if provided, otherwise exchange name
      const identifier = integrationId || exchangeId;

      const res = await authorizedAxios(token).get(`/integrations/${identifier}`);
      return res.data.integration;
    } catch (error) {
      console.error(
        `[useGetExchange] Failed to fetch exchange ${exchangeId || integrationId}:`,
        error
      );
      throw error;
    }
  };

  const {
    isLoading: isQueryLoading,
    isIdle,
    error,
    data,
    refetch,
  }: UseQueryResult<Exchange, unknown> = useQuery({
    queryKey: useExchangeQueryKey(exchangeId, integrationId),
    queryFn: getExchange,
    enabled: !!email && !!(exchangeId || integrationId),
    refetchOnWindowFocus,
    staleTime,
    keepPreviousData: true,
  });

  return {
    isQueryLoading,
    isIdle,
    error,
    exchange: data,
    refetch,
  };
};
